import React, { useState, useEffect } from 'react';
import { 
  Package, Check, CheckSquare, Square, Save, 
  Loader2, AlertCircle, ChevronDown, ChevronUp
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const ProcurementChecklist = ({ orderId, adminToken, orderStatus }) => {
  const { toast } = useToast();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState({});

  // Only show when order is in procurement status
  const isVisible = orderStatus === 'procurement';

  useEffect(() => {
    if (isVisible && orderId && adminToken) {
      loadChecklist();
    }
  }, [orderId, adminToken, isVisible]);

  const loadChecklist = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${orderId}/procurement-checklist`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setChecklist(data.checklist);
          // Keep all products collapsed by default
          setExpandedProducts({});
        }
      }
    } catch (error) {
      console.error('Error loading procurement checklist:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare la checklist",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleComponentToggle = (productIndex, componentKey) => {
    const updatedChecklist = { ...checklist };
    const product = updatedChecklist.items[productIndex];
    const component = product.components.find(c => c.key === componentKey);
    
    if (component) {
      component.checked = !component.checked;
      component.checked_at = component.checked ? new Date().toISOString() : null;
    }
    
    setChecklist(updatedChecklist);
  };

  const handleNotesChange = (productIndex, componentKey, notes) => {
    const updatedChecklist = { ...checklist };
    const product = updatedChecklist.items[productIndex];
    const component = product.components.find(c => c.key === componentKey);
    
    if (component) {
      component.notes = notes;
    }
    
    setChecklist(updatedChecklist);
  };

  const handleSelectAll = (productIndex) => {
    const updatedChecklist = { ...checklist };
    const product = updatedChecklist.items[productIndex];
    const allChecked = product.components.every(c => c.checked);
    
    product.components.forEach(component => {
      component.checked = !allChecked;
      component.checked_at = !allChecked ? new Date().toISOString() : null;
    });
    
    setChecklist(updatedChecklist);
  };

  const handleSelectAllGlobal = () => {
    const updatedChecklist = { ...checklist };
    const allChecked = updatedChecklist.items.every(item => 
      item.components.every(c => c.checked)
    );
    
    updatedChecklist.items.forEach(item => {
      item.components.forEach(component => {
        component.checked = !allChecked;
        component.checked_at = !allChecked ? new Date().toISOString() : null;
      });
    });
    
    setChecklist(updatedChecklist);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${orderId}/procurement-checklist`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(checklist)
        }
      );

      if (response.ok) {
        toast({
          title: "Salvato!",
          description: "Checklist approvvigionamento aggiornata",
        });
      } else {
        throw new Error('Errore salvataggio');
      }
    } catch (error) {
      console.error('Error saving checklist:', error);
      toast({
        title: "Errore",
        description: "Impossibile salvare la checklist",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleProductExpansion = (productIndex) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productIndex]: !prev[productIndex]
    }));
  };

  const getProgress = (items) => {
    if (!items || items.length === 0) return { checked: 0, total: 0 };
    
    let checked = 0;
    let total = 0;
    
    items.forEach(item => {
      item.components.forEach(component => {
        total++;
        if (component.checked) checked++;
      });
    });
    
    return { checked, total };
  };

  const getProductProgress = (components) => {
    if (!components || components.length === 0) return { checked: 0, total: 0 };
    
    const checked = components.filter(c => c.checked).length;
    return { checked, total: components.length };
  };

  if (!isVisible) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
          <span className="ml-2 text-gray-600">Caricamento checklist...</span>
        </div>
      </div>
    );
  }

  if (!checklist) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center text-yellow-600">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span>Impossibile caricare la checklist</span>
        </div>
      </div>
    );
  }

  const globalProgress = getProgress(checklist.items);
  const allGlobalChecked = globalProgress.checked === globalProgress.total && globalProgress.total > 0;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden" data-testid="procurement-checklist">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center text-white">
            <Package className="w-5 h-5 mr-2" />
            <h2 className="text-lg font-semibold">Checklist Approvvigionamento</h2>
          </div>
          <button
            onClick={handleSelectAllGlobal}
            className="flex items-center px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-sm rounded-lg transition-colors"
            data-testid="select-all-global"
          >
            {allGlobalChecked ? (
              <CheckSquare className="w-4 h-4 mr-1.5" />
            ) : (
              <Square className="w-4 h-4 mr-1.5" />
            )}
            Seleziona tutto
          </button>
        </div>
        
        {/* Global Progress Bar */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-white/90 text-sm mb-1">
            <span>Progresso totale</span>
            <span>{globalProgress.checked}/{globalProgress.total} componenti</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2">
            <div 
              className="bg-white rounded-full h-2 transition-all duration-300"
              style={{ width: `${globalProgress.total > 0 ? (globalProgress.checked / globalProgress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="divide-y divide-gray-100">
        {checklist.items.map((item, productIndex) => {
          const productProgress = getProductProgress(item.components);
          const isExpanded = expandedProducts[productIndex];
          const allProductChecked = productProgress.checked === productProgress.total;

          return (
            <div key={productIndex} className="border-b border-gray-100 last:border-b-0">
              {/* Product Header */}
              <div 
                className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleProductExpansion(productIndex)}
                data-testid={`product-header-${productIndex}`}
              >
                <div className="flex items-center flex-1">
                  <button className="mr-3 text-gray-400">
                    {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  <div>
                    <h3 className="font-medium text-gray-900">{item.product_name}</h3>
                    <p className="text-sm text-gray-500">
                      {item.quantity && `${item.quantity} pz`}
                      {item.format && ` • ${item.format}`}
                    </p>
                  </div>
                </div>
                
                {/* Product Progress */}
                <div className="flex items-center gap-4">
                  <div className="w-32">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>{productProgress.checked}/{productProgress.total}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div 
                        className={`rounded-full h-1.5 transition-all duration-300 ${
                          allProductChecked ? 'bg-green-500' : 'bg-cyan-500'
                        }`}
                        style={{ width: `${(productProgress.checked / productProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                  {allProductChecked && (
                    <Check className="w-5 h-5 text-green-500" />
                  )}
                </div>
              </div>

              {/* Components List (Expandable) */}
              {isExpanded && (
                <div className="px-6 pb-4">
                  {/* Select All for this product */}
                  <div className="mb-3 pb-3 border-b border-gray-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectAll(productIndex);
                      }}
                      className="flex items-center text-sm text-cyan-600 hover:text-cyan-700"
                      data-testid={`select-all-product-${productIndex}`}
                    >
                      {allProductChecked ? (
                        <CheckSquare className="w-4 h-4 mr-1.5" />
                      ) : (
                        <Square className="w-4 h-4 mr-1.5" />
                      )}
                      Seleziona tutti per questo prodotto
                    </button>
                  </div>

                  {/* Components Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {item.components.map((component) => (
                      <div 
                        key={component.key}
                        className={`flex items-start p-3 rounded-lg border transition-colors ${
                          component.checked 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`component-${productIndex}-${component.key}`}
                      >
                        {/* Checkbox */}
                        <button
                          onClick={() => handleComponentToggle(productIndex, component.key)}
                          className={`flex-shrink-0 w-5 h-5 rounded border-2 mr-3 mt-0.5 flex items-center justify-center transition-colors ${
                            component.checked 
                              ? 'bg-green-500 border-green-500 text-white' 
                              : 'border-gray-300 hover:border-cyan-500'
                          }`}
                        >
                          {component.checked && <Check className="w-3 h-3" />}
                        </button>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`font-medium text-sm ${
                              component.checked ? 'text-green-800' : 'text-gray-700'
                            }`}>
                              {component.label}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              component.checked 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {component.checked ? '✓ Pronto' : '⏳ In attesa'}
                            </span>
                          </div>
                          
                          {/* Notes Input */}
                          <input
                            type="text"
                            value={component.notes || ''}
                            onChange={(e) => handleNotesChange(productIndex, component.key, e.target.value)}
                            placeholder="Note..."
                            className="w-full text-sm px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 bg-white"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`notes-${productIndex}-${component.key}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer with Save Button */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {checklist.updated_at && (
            <span>
              Ultimo aggiornamento: {new Date(checklist.updated_at).toLocaleString('it-IT')}
              {checklist.updated_by && ` da ${checklist.updated_by}`}
            </span>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50"
          data-testid="save-checklist"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Salva Modifiche
        </button>
      </div>
    </div>
  );
};

export default ProcurementChecklist;
